/**
 * Organizations admin list (ADMIN-PR1-b-orgs) — GET /v1/admin/organizations
 * with offset/limit pagination. Rows link to the org detail screen.
 *
 * DERIVED view: oxbo-auth has no real org registry, so `name`/`created_at` are
 * best-effort and labelled non-authoritative.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { listOrganizations, type OrganizationListResponse } from "../auth/adminApi";
import { errorMessage, useAsync } from "../lib/useAsync";
import { EmptyState, ErrorBanner, PageHeader, Spinner } from "../components/ui";

const PAGE_SIZE = 25;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export function OrgsPage(): ReactNode {
  const [offset, setOffset] = useState(0);
  const { data, loading, error } = useAsync<OrganizationListResponse>(
    () => listOrganizations({ limit: PAGE_SIZE, offset }),
    [offset],
  );

  const total = data?.total ?? 0;
  const showingTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <div>
      <PageHeader title="Organizations" />
      <p className="mb-3 text-xs text-slate-500">
        Derived view — organization <em>name</em> and <em>created</em> are best-effort and not
        authoritative (oxbo-auth has no org registry).
      </p>

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBanner message={errorMessage(error)} />
      ) : !data || data.organizations.length === 0 ? (
        <EmptyState message="No organizations." />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Org ID</th>
                  <th className="px-3 py-2 font-medium">Members</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.organizations.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <Link to={`/orgs/${o.id}`} className="text-oxbo hover:underline">
                        {o.name ?? <span className="italic text-slate-400">unnamed</span>}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{o.id}</td>
                    <td className="px-3 py-2 text-slate-600">{o.member_count}</td>
                    <td className="px-3 py-2 text-slate-500">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
            <span>
              {total === 0 ? "0" : `${offset + 1}–${showingTo}`} of {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={showingTo >= total}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
